import { cn } from "@/lib/utils";

const ITEMS_PER_GROUP = 4;

interface GroupedListGroup<T> {
  title: string;
  items: T[];
}

interface GroupedListRendererProps<T> {
  groups: GroupedListGroup<T>[];
  getItemKey: (item: T, index: number) => string;
  renderItem: (item: T) => React.ReactNode;
  onSelectItem: (item: T) => void;
  expandedGroups: Record<string, boolean>;
  onToggleExpand: (groupTitle: string) => void;
  selectedIndex?: number;
  onSelectByIndex?: (globalIndex: number) => void;
}

export function GroupedListRenderer<T>({
  groups,
  getItemKey,
  renderItem,
  onSelectItem,
  expandedGroups,
  onToggleExpand,
  selectedIndex = -1,
  onSelectByIndex,
}: GroupedListRendererProps<T>) {
  const flattenedStructure = groups
    .filter((group) => group.items.length > 0)
    .map((group) => {
      const isExpanded = expandedGroups[group.title];
      const firstFour = group.items.slice(0, ITEMS_PER_GROUP);
      const hasMore = group.items.length > ITEMS_PER_GROUP;
      const groupItems = isExpanded ? group.items : firstFour;

      return {
        group,
        groupItems,
        hasMore,
        isExpanded,
      };
    });

  let flatIndex = 0;

  return flattenedStructure.map((groupData, groupIndex) => {
    const { group, groupItems, hasMore, isExpanded } = groupData;
    const groupStartIndex = flatIndex;
    flatIndex += groupItems.length;
    const showMoreIndex = hasMore && !isExpanded ? flatIndex++ : null;

    return (
      <div key={group.title}>
        {groupIndex > 0 && <hr className="bg-border my-2 h-px" />}
        <div className="font-family-fancy px-2 py-1 text-xs font-bold text-gray-500">
          {group.title}
        </div>
        {groupItems.map((item, itemIndex) => (
          <button
            key={getItemKey(item, itemIndex)}
            className={cn(
              "hover:bg-background-10 min-h-[32px] w-full cursor-pointer rounded px-2 py-1 text-left text-sm break-words transition-colors",
              selectedIndex === groupStartIndex + itemIndex &&
                "bg-background-10",
            )}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              if (onSelectByIndex) {
                onSelectByIndex(groupStartIndex + itemIndex);
              } else {
                onSelectItem(item);
              }
            }}
            type="button"
          >
            {renderItem(item)}
          </button>
        ))}
        {hasMore && !isExpanded && (
          <button
            className={cn(
              "hover:bg-background-10 min-h-[32px] w-full cursor-pointer rounded px-2 py-1 text-left text-xs break-words transition-colors",
              selectedIndex === showMoreIndex && "bg-background-10",
            )}
            onClick={() => onToggleExpand(group.title)}
            type="button"
          >
            ... {group.items.length - ITEMS_PER_GROUP} more results
          </button>
        )}
      </div>
    );
  });
}
